import React from 'react'
import { FieldErrors, SubmitHandler } from 'react-hook-form'
import get from 'lodash/get'
import { ControlledHiddenInput, ControlledTextInput } from 'transfix-form/components'
import { Box, Button, Typography } from 'transfix-ui/core'
import { IConfigGroupMapping } from 'types/graphqlTypes'
import { TEdiConfigGroupMappingSchema } from './EdiConfigGroupMappingSchema'
import { useEdiConfigGroupMappingForm } from './useEdiConfigGroupMappingForm'

interface IEdiConfigGroupMappingFormProps {
  configGroupMapping: IConfigGroupMapping | null
  connectionConfigId: string | undefined
  shipperName: string | undefined
  onCancel: () => void
  onSubmitSuccess: SubmitHandler<TEdiConfigGroupMappingSchema>
}

export function EdiConfigGroupMappingForm({
  configGroupMapping,
  connectionConfigId,
  onCancel,
  onSubmitSuccess,
  shipperName,
}: IEdiConfigGroupMappingFormProps) {
  const { control, errors, handleSubmit } = useEdiConfigGroupMappingForm({
    defaultValues: {
      configGroupId: configGroupMapping?.configGroup?.id,
      configGroupMappingId: configGroupMapping?.id,
      configGroupName: configGroupMapping?.configGroup?.name,
      isaSenderId: configGroupMapping?.isaSenderId,
    },
  })

  const getErrorMessage = <T extends FieldErrors<TEdiConfigGroupMappingSchema>, K extends keyof T>(
    errors: T,
    keyName: K
  ): string | undefined => {
    return get(errors, [keyName, 'message'])
  }

  const onSubmitError = () => {}
  const submitHandler = handleSubmit(onSubmitSuccess, onSubmitError)

  const action = configGroupMapping ? 'Update' : 'Create'

  // TODO: how to show spinners while submitHandler is running, and then stop once it finishes?
  return (
    <form onSubmit={submitHandler} data-test='ConfigGroupMappingForm'>
      <Typography>
        {`${action} config group mapping for ${shipperName} on connection config ${connectionConfigId}`}
      </Typography>
      <ControlledHiddenInput control={control} name='configGroupId' />
      <ControlledHiddenInput control={control} name='configGroupMappingId' />
      <ControlledTextInput
        control={control}
        name='configGroupName'
        TextInputProps={{
          label: 'Config Group Name',
          dataTest: 'config-group-name',
          error: Boolean(getErrorMessage(errors, 'configGroupName')),
          validationMessage: getErrorMessage(errors, 'configGroupName'),
          required: true,
        }}
      />
      <ControlledTextInput
        control={control}
        name='isaSenderId'
        TextInputProps={{
          label: 'ISA Sender ID',
          dataTest: 'isa-sender-id',
          error: Boolean(getErrorMessage(errors, 'isaSenderId')),
          validationMessage: getErrorMessage(errors, 'isaSenderId'),
          required: true,
        }}
      />
      <Box paddingTop={2}>
        <Button type='submit' color='primary' data-test='submit-button'>
          {configGroupMapping?.id ? 'Update' : 'Save'}
        </Button>{' '}
        <Button onClick={onCancel} data-test='cancel-button'>
          Cancel
        </Button>
      </Box>
    </form>
  )
}
